import { useEffect, useRef, useState } from 'react';
import { PHOTOS, MANIFESTO } from '@/constants/content';
import { useReveal } from '@/lib/useReveal';

// The Vision, emotional anchor section immediately after the hero.
// Features gentle parallax on the executive portrait + slow luxurious reveal.
export default function Vision() {
  const ref = useReveal();
  const imgWrapRef = useRef(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const el = imgWrapRef.current;
    if (!el) return;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight || 1;
        const progress = 1 - (rect.top + rect.height / 2) / vh;
        setOffset(Math.max(-40, Math.min(40, progress * 40)));
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section
      ref={ref}
      className="reveal py-32 lg:py-56 bg-ivory relative"
      aria-labelledby="vision-title"
      data-testid="home-vision"
    >
      <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20 grid lg:grid-cols-12 gap-16 lg:gap-24 items-center">
        <div ref={imgWrapRef} className="lg:col-span-5 order-2 lg:order-1">
          <div className="relative rounded-3xl overflow-hidden luxury-shadow">
            <img
              src={PHOTOS.about}
              alt="Toia Wallace, founder, a portrait for the vision"
              className="w-full aspect-[3/2] object-cover object-[30%_top] md:object-left-top will-change-transform"
              style={{ transform: `translate3d(0, ${offset}px, 0)` }}
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-matte/30 via-transparent to-transparent" aria-hidden />
            <div className="absolute bottom-8 left-8 right-8 flex items-center justify-between text-ivory font-label text-[10px] tracking-widest uppercase">
              <span>The Vision</span>
              <span>Founder’s Portrait</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 order-1 lg:order-2">
          <div className="eyebrow text-taupe mb-10">{MANIFESTO.eyebrow}</div>
          <h2 id="vision-title" className="font-serif-display text-5xl md:text-6xl lg:text-7xl tracking-tightest leading-[0.95] text-matte">
            {MANIFESTO.title}
          </h2>
          <p className="mt-12 font-serif-display text-2xl md:text-3xl lg:text-[2.35rem] leading-[1.35] tracking-tight text-matte/85 max-w-2xl">
            {MANIFESTO.body}
          </p>
          <div className="mt-12 flex items-center gap-6">
            <span className="hairline-gold h-px w-16" aria-hidden />
            <span className="eyebrow text-taupe">{MANIFESTO.signature}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
